// LLM generated - Model Claude 4 Sonnet
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { pinMessage, unpinMessage } from '../live-activity-service.service.api';
import { logError } from '~/common/utils/log';

type UsePinMessageProps = {
  chatId?: string;
  onSuccess?: () => void;
};

/**
 * Hook for pinning/unpinning messages.
 * Note: We don't invalidate queries here - Pusher's message-updated event
 * will update the cache via handleMessageUpdatedFromPusher.
 */
export const usePinMessage = ({ chatId, onSuccess }: UsePinMessageProps) => {
  const { t } = useTranslation('chat');

  const pinMutation = useMutation({
    mutationFn: (messageId: string) => {
      if (!chatId) throw new Error('Chat ID not available');
      return pinMessage(chatId, messageId);
    },
    onSuccess: () => {
      // Pusher will handle cache update via onMessageUpdated
      onSuccess?.();

      showNotification({
        title: t('notifications.postPinnedSuccess'),
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to pin message:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.postPinFailed'),
        color: 'red',
      });
    },
  });

  const unpinMutation = useMutation({
    mutationFn: (messageId: string) => {
      if (!chatId) throw new Error('Chat ID not available');
      return unpinMessage(chatId, messageId);
    },
    onSuccess: () => {
      // Pusher will handle cache update via onMessageUpdated
      onSuccess?.();

      showNotification({
        title: t('notifications.postUnpinnedSuccess'),
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to unpin message:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.postUnpinFailed'),
        color: 'red',
      });
    },
  });

  return {
    pinMessage: pinMutation.mutate,
    unpinMessage: unpinMutation.mutate,
    isPinning: pinMutation.isLoading,
    isUnpinning: unpinMutation.isLoading,
    isLoading: pinMutation.isLoading || unpinMutation.isLoading,
  };
};
